import axios from "axios";
import Links from "@/shared/constants/Links";

/** Defense event and student-roster API. */

const base = Links.defenses.base;

const Defenses = {
    /**
     * Create a department defense event for a date and session. HOD only.
     */
    create: async (data) => {
        return axios.post(base, data);
    },

    /**
     * List events, optionally filtered by department and/or student decision.
     * HOD requests must not include department; the backend restricts them to their own.
     * @param {{department?: number|string|null, status?: string|null}} filters
     */
    all: async (filters = {}) => {
        const params = {};
        if (filters.department) params.department = filters.department;
        if (filters.status) params.status = filters.status;
        return axios.get(base, { params });
    },

    /**
     * Get an event including its student roster.
     * @param {number|string} id
     */
    get: async (id) => {
        return axios.get(`${base}/${id}`);
    },

    /** Get the per-progress-status allocation balance for a defense. HOD only. */
    allocations: async (id) => {
        return axios.get(`${base}/${id}/allocations`);
    },

    /** List allocation purchases created for a defense. HOD only. */
    allocationPurchases: async (id) => {
        return axios.get(`${base}/${id}/allocation-purchases`);
    },

    /** Start an HOD-funded allocation purchase. */
    createAllocationPurchase: async (id, data) => {
        return axios.post(`${base}/${id}/allocation-purchases`, data);
    },

    /** Cancel a pending allocation purchase that has no recorded payment. */
    cancelAllocationPurchase: async (defenseId, purchaseId) => {
        return axios.delete(`${base}/${defenseId}/allocation-purchases/${purchaseId}`);
    },

    /** Download a cleared allocation purchase invoice as a PDF. */
    downloadAllocationInvoice: async (defenseId, purchaseId) => {
        return axios.get(`${base}/${defenseId}/allocation-purchases/${purchaseId}/invoice`, {
            responseType: "blob",
        });
    },

    /**
     * Download the defense roster as a PDF. HOD and DEAN_SPGS only.
     * The backend applies the HOD's department scope.
     * @param {number|string} id
     * @param {number|string|null} progressStatusId Optional student progress-status filter
     */
    downloadRosterReport: async (id, progressStatusId = null) => {
        const config = {responseType: "blob"};
        if (progressStatusId !== null && progressStatusId !== undefined && progressStatusId !== '') {
            config.params = {progress_status: progressStatusId};
        }
        return axios.get(`${base}/${id}/roster-report`, config);
    },

    /**
     * Update a draft defense, always resending its immutable session. HOD only.
     * @param {number|string} id
     * @param {object} data DefenseRequest fields
     */
    update: async (id, data) => {
        return axios.put(`${base}/${id}`, data);
    },

    /**
     * Submit an event roster to the SPGS dean. HOD only.
     * The event becomes read-only after submission.
     */
    submit: async (id) => {
        return axios.patch(`${base}/${id}/submit`);
    },

    /**
     * Cancel a submitted, unreviewed event. DEAN_SPGS only.
     * @param {number|string} id
     * @param {string} reason
     */
    cancel: async (id, reason) => {
        return axios.patch(`${base}/${id}/cancel`, { reason });
    },

    /**
     * Reschedule a submitted, unreviewed event. DEAN_SPGS only.
     * @param {number|string} id
     * @param {{date: string, reason: string}} data
     */
    reschedule: async (id, data) => {
        return axios.patch(`${base}/${id}/reschedule`, data);
    },

    /**
     * Add a student to an event roster. HOD only.
     */
    addStudent: async (defenseId, data) => {
        return axios.post(`${base}/${defenseId}/students`, data);
    },

    /**
     * Remove a pending roster entry. HOD only.
     */
    removeStudent: async (defenseId, defenseStudentId) => {
        return axios.delete(`${base}/${defenseId}/students/${defenseStudentId}`);
    },

    /** Approve a pending or rejected roster entry. DEAN_SPGS only. */
    approveStudent: async (defenseId, defenseStudentId) => {
        return axios.patch(`${base}/${defenseId}/students/${defenseStudentId}/approve`);
    },

    /** Reject a pending or approved roster entry. DEAN_SPGS only. */
    rejectStudent: async (defenseId, defenseStudentId, reason) => {
        return axios.patch(`${base}/${defenseId}/students/${defenseStudentId}/reject`, { reason });
    },

    /** Assign or replace the event PG representative. DEAN_SPGS only. */
    assignPgRepresentative: async (defenseId, lecturerId) => {
        return axios.put(`${base}/${defenseId}/pg-representative`, { lecturerId });
    },

    /**
     * Delete an event before HOD submission. HOD only.
     * @param {number|string} id
     */
    delete: async (id) => {
        return axios.delete(`${base}/${id}`);
    },
};

export default Defenses;
